// CHG-131 REQ-CP-024/025/026: ProfileModalProvider + useProfileModal
// - AppLayout 최상단에서 자식 트리를 감싸 어디서나 useProfileModal()로 열 수 있게 한다.
// - 본인 클릭 시 ProfileModal을 띄우지 않고 /profile로 navigate (편집 흐름)
// - 다른 사용자 클릭 시 ProfileModal 본문 노출 (한 번에 1개)
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import ProfileModal from './ProfileModal';

interface ProfileModalContextValue {
  openProfileModal: (userId: string) => void;
  closeProfileModal: () => void;
}

const ProfileModalContext = createContext<ProfileModalContextValue | null>(null);

export function ProfileModalProvider({ children }: { children: ReactNode }) {
  const [targetUserId, setTargetUserId] = useState<string | null>(null);

  // 본인/타인 구분 없이 모달 노출 — /profile 편집 페이지는 GNB 메뉴에서 진입.
  // 빠르게 다른 사용자 연속 클릭 시 setTargetUserId(userId)로 즉시 교체 — 모달이 한 번에 1개만 유지됨.
  const openProfileModal = useCallback((userId: string) => {
    if (!userId) return;
    setTargetUserId(userId);
  }, []);

  const closeProfileModal = useCallback(() => setTargetUserId(null), []);

  const value = useMemo<ProfileModalContextValue>(
    () => ({ openProfileModal, closeProfileModal }),
    [openProfileModal, closeProfileModal],
  );

  return (
    <ProfileModalContext.Provider value={value}>
      {children}
      {targetUserId && (
        <ProfileModal
          // key로 userId 변경 시 즉시 새 데이터 fetching + 이전 상태 reset
          key={targetUserId}
          userId={targetUserId}
          onClose={closeProfileModal}
        />
      )}
    </ProfileModalContext.Provider>
  );
}

export function useProfileModal(): ProfileModalContextValue {
  const ctx = useContext(ProfileModalContext);
  if (!ctx) {
    throw new Error('useProfileModal must be used inside ProfileModalProvider');
  }
  return ctx;
}
